import { AcceptTermsCheckbox, AsyncActionButton, ESessionStorageKeys, ExpandableSection, FilteredCreditApplication, Loader, Merchant, reportErrorToConsole, SinInput, useFeatureFlags, useSubmitHardHit } from '@applyonline/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingProgram } from '@if/core/types'
import {
  formatCurrency,
  formatCurrencyNoDecimals,
  formatCurrencyRoundedUpToNoDecimals,
} from '@if/core/utils'
import { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useComputeEstimatedPayment } from '../../../../api/compute-api'
import { ManageCoappButton } from '../../../../components'
import RemoveCoappButton from '../../../../components/RemoveCoappButton'
import {
  getPrequalifiedAmount,
  getPrequalifiedMaxTerm,
  mustAskApplicantSIN,
  mustAskCoapplicantSIN,
} from '../../../../data/credit-application-selectors'
import { trackFunnelStepEvent, useTrackStepReachedEvent } from '../../../../services/trackingEvents'
import { Constants } from '../../../../types'
import { buildEditSinAndLoanAmountSchema, EditSinAndLoanAmount } from '../../../../types/Schemas/PrequalificationSchema'
import { ETrackedEvents } from '../../../../types/tracking'
import ApprovedSoftHitAndHardHitFooter from '../../components/ApprovedSoftHitAndHardHitFooter'

interface Props {
  creditApp: FilteredCreditApplication
  merchant: Merchant
}

const ApprovedPrequalForm = ({ creditApp, merchant }: Props) => {
  const { t } = useTranslation()

  const initialPrequalifiedAmount = getPrequalifiedAmount(creditApp)
  const initialPrequalifiedTerm = getPrequalifiedMaxTerm(creditApp)

  const [estimatedPaymentResult, isFetchingEstimatedPayment, isEstimatedPaymentError] = useComputeEstimatedPayment(creditApp.id)
  const maxLoanTerm: number = estimatedPaymentResult?.term ?? initialPrequalifiedTerm

  const askApplicantSIN = mustAskApplicantSIN(creditApp)
  const askCoapplicantSIN = mustAskCoapplicantSIN(creditApp)
  const { requestedLoanAmount } = creditApp
  const maxLoanAmout = creditApp.prequalificationDecision.maxLoanAmount

  const shouldProposeCoapplicant = maxLoanAmout < requestedLoanAmount && creditApp.applicant.hasCreditReport && !Constants.MerchantsThatForbidHavingCoapplicants.includes(creditApp.merchantId)
  const hasCoapplicant = creditApp.coapplicant != null

  const [featureFlags, isFetchingFeatureFlags] = useFeatureFlags(EFinancingProgram.Personal)
  // Ticket #67167 - gate the merged-consent behind a feature switch.
  // When the flag is off, keep the legacy behavior: show the hard hit consent checkbox here.
  const useNewConsentFlow = featureFlags?.useNewApplyConsentFlow ?? false

  useTrackStepReachedEvent(creditApp, ETrackedEvents.PrequalSuccess)

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<EditSinAndLoanAmount>({
    mode: 'onBlur',
    defaultValues: {
      needsApplicantSin: askApplicantSIN,
      needsCoapplicantSin: askCoapplicantSIN === true,
      requestedLoanAmount: initialPrequalifiedAmount,
      id: creditApp.id,
    },
    resolver: yupResolver(buildEditSinAndLoanAmountSchema(creditApp.prequalificationDecision.maxLoanAmount)),
  })

  const [submitHardHit, isSubmitting] = useSubmitHardHit(EFinancingProgram.Personal)
  const innerSubmit = useCallback(
    (data: EditSinAndLoanAmount) => {
      trackFunnelStepEvent(ETrackedEvents.ConsentHardHit, creditApp.merchantId)
      submitHardHit(data).catch(reportErrorToConsole)
      sessionStorage.removeItem(ESessionStorageKeys.PREQUAL_INFO)
    },
    [submitHardHit, creditApp.merchantId],
  )

  if (isFetchingEstimatedPayment || isEstimatedPaymentError || isFetchingFeatureFlags) {
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  }

  return (
    <main className="general-message" style={{ display: 'block' }}>
      <form onSubmit={handleSubmit(innerSubmit)}>
        <h1 className="h3" style={{ lineHeight: 1.5 }}>
          {!estimatedPaymentResult?.isPromotional && (
            <Trans
              i18nKey="prequalification.prequalified"
              values={{
                requestedAmount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount),
                minMonthlyPayment: formatCurrencyNoDecimals(estimatedPaymentResult?.minPaymentAmount ?? 0),
                maxMonthlyPayment: formatCurrencyNoDecimals(estimatedPaymentResult?.maxPaymentAmount ?? 0),
              }}
            />
          )}

          {estimatedPaymentResult?.isPromotional && (
            <Trans
              i18nKey="prequalification.prequalifiedVariableInterest"
              values={{
                requestedAmount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount),
                monthlyPayment: formatCurrency(estimatedPaymentResult.minPaymentAmount),
              }}
            />
          )}
        </h1>
        <div className="paragraph">
          <div className="information">
            <Trans i18nKey="prequalification.loanDetails.mainDetails" />
          </div>
          <ExpandableSection toggleText="prequalification.loanDetails.learnMore">
            <p
              style={{
                fontStyle: 'italic',
                border: '1px solid #08abf0',
                borderRadius: '1.5rem',
                textAlign: 'center',
                padding: '1.5rem',
              }}
            >
              {t('prequalification.loanDetails.learnMoreDetails')}
            </p>
          </ExpandableSection>

          {shouldProposeCoapplicant && (
            <div style={{ marginTop: '32px', marginBottom: '32px' }}>
              <div className="strong">{t('prequalification.addCoapplicant.title')}</div>
              <div className="btn-group" style={{ marginTop: '16px' }}>
                {hasCoapplicant && <RemoveCoappButton creditAppId={creditApp.id} />}
                <ManageCoappButton creditAppId={creditApp.id} useChangeLabel={hasCoapplicant} merchantId={creditApp.merchantId} isHardDecline={creditApp.finalDecision.isHardDecline}/>
              </div>
            </div>
          )}

          {askApplicantSIN && (
            <SinInput
              error={errors.applicantSin}
              id="applicantSin"
              reason="prequalification.applicantNewToCredit"
              {...register('applicantSin')}
              mandatory={true}

            />
          )}

          {askCoapplicantSIN && (
            <SinInput
              error={errors.coapplicantSin}
              id="coapplicantSin"
              reason="prequalification.coapplicantNewToCredit"
              {...register('coapplicantSin')}
              mandatory={true}
            />
          )}

          {merchant.supportsPromotions && creditApp.merchantPaymentPlanId != null && (
            <div style={{ marginTop: '32px' }}>
              <Trans i18nKey="prequalification.pleaseNote" />
            </div>
          )}
          {!useNewConsentFlow && (
            <>
              <div style={{ marginTop: '32px' }}>{t('prequalification.pullCreditReport')}</div>
              <AcceptTermsCheckbox
                id="acceptConditions"
                termId={`${String(t(hasCoapplicant ? 'prequalification.acceptConditionsWithCoapplicant' : 'prequalification.acceptConditions'))}²`}
                msgOnInvalid={t('common.acceptConditions')}
              />
            </>
          )}
        </div>
        <div className="btn-group mobile-reversed justify-end" style={{ marginTop: '32px' }}>
          <AsyncActionButton type="submit" isPending={isSubmitting} primary style={{ justifyContent: 'center' }}>
            <span>
              {shouldProposeCoapplicant
                ? t('common.continueWith', { amount: formatCurrencyRoundedUpToNoDecimals(initialPrequalifiedAmount) })
                : t(useNewConsentFlow ? 'prequalification.continueWithApplication' : 'prequalification.continue')}
            </span>
            <i className="fa-regular fa-arrow-right" />
          </AsyncActionButton>
        </div>
        <ApprovedSoftHitAndHardHitFooter
          merchantPaymentPlan={creditApp.merchantPaymentPlan}
          minInterestRate={estimatedPaymentResult?.interestRate ?? creditApp.prequalificationDecision.minInterestRate}
          hasCoapplicant={hasCoapplicant}
          maxLoanTerm={maxLoanTerm}
          useNewConsentFlow={useNewConsentFlow}
        />
      </form>
    </main>
  )
}

export default ApprovedPrequalForm
